import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { getOptionsQueryParams } from "@app/class/Utilities";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { lastValueFrom } from "rxjs";
import { ProductVendor } from "../enums/CameraProductType";
import { IAvailableDailyFootage, IFootageHistory } from "../models/IAvailableDailyFootage";
import { IAvailableFootageCalendar } from "../models/IAvailableFootageCalendar";
import { DateService } from "@app/services/date.service";
import { VideoStreamType } from "../enums/VideoStreamType";

@Injectable({
    providedIn: 'root'
})
export class CameraFootageService {
    constructor(private readonly httpClient: HttpClient, private readonly dateService: DateService) { }

    async getLatestCalendarFootageHistory(
        peripheralSerial: string,
        concatenated = false,
        date: Date = null,
        invalidateCache = false
    ): Promise<IAvailableFootageCalendar> {
        const options = getOptionsQueryParams({ minsToLive: 60, invalidateCache, concatenated, date });

        return await lastValueFrom(this.httpClient
            .get<IAvailableFootageCalendar>(`/api/cameras/${peripheralSerial}/calendar`, options));
    }

    async getFootageHistoryForDay(peripheralSerial: string, date: Date, invalidateCache = false): Promise<IAvailableDailyFootage[]> {
        const options = getOptionsQueryParams({ minsToLive: 60, invalidateCache, date });

        return await lastValueFrom(this.httpClient
            .get<IAvailableDailyFootage[]>(`/api/cameras/${peripheralSerial}/history`, options));
    }

    async getCameraFootageHistory(serial: string, minDate: Date): Promise<IFootageHistory[]> {
        const options = getOptionsQueryParams({ minsToLive: 60, serial, minDate });

        return await lastValueFrom(this.httpClient
            .get<IFootageHistory[]>(`/api/cameras/${serial}/footagehistory`, options));
    }

    getMaxRequestFootageLength(camera: IPeripheral): number {
        return camera.Product?.ProductMetadata?.CameraFeatures?.VideoRequest?.MaxRequestLength;
    }

    isFootageFunctionalityAvailable(peripherals: IPeripheral[]): boolean {
        return peripherals.some(peripheral => {
            const isVisionTrack = peripheral.Product.GroupId === ProductVendor.VisionTrack;
            const cameraIsNotLiveStream = !peripheral.isLiveStreamCam;
            const cameraHasVideoRequest = peripheral.Product.ProductMetadata?.CameraFeatures?.VideoRequest != null;

            return (isVisionTrack && cameraIsNotLiveStream) || cameraHasVideoRequest;
        })
    }

    isFootageLengthFunctionalityAvailable(peripherals: IPeripheral[]): boolean {
        return peripherals.some(peripheral => peripheral.Product?.ProductMetadata?.CameraFeatures?.VideoRequest?.MaxRequestLength > 0);
    }

    async getSelectableDatesFromCameraFootageHistory(serial: string, minDate: Date): Promise<Date[]> {
        const history = await this.getCameraFootageHistory(serial, minDate);
        const usedDates = new Set<string>();

        history.forEach(({ StartTime, EndTime }) => {
            let currentDate = new Date(StartTime);
            const endDate = EndTime ? new Date(EndTime) : currentDate;

            while (currentDate <= endDate) {
                if (currentDate >= minDate) {
                    usedDates.add(currentDate.toISOString().split('T')[0]);
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
        });

        return [...usedDates].map(date => new Date(date));
    }

    mapFootageHistoryToAvailableDailyFootage(footageHistory: IFootageHistory[]): IAvailableDailyFootage[] {
        const result: IAvailableDailyFootage[] = [];
        const now = new Date();

        for (const item of footageHistory) {
            if (item.EndTime) {
                result.push({ 
                    Start: item.StartTime,
                    End: item.EndTime,
                    Channel: item.ChannelId,
                    StreamType: VideoStreamType.Main
                });
            } else { 
                if (this.dateService.isSameDate(now, item.StartTime)) {
                    result.push({ 
                        Start: item.StartTime,
                        End: now,
                        Channel: item.ChannelId,
                        StreamType: VideoStreamType.Main
                    });
                }
                else {
                    const start = new Date(item.StartTime);
                    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 23, 59, 59);
                    result.push({ 
                        Start: start,
                        End: end,
                        Channel: item.ChannelId,
                        StreamType: VideoStreamType.Main
                    });

                    const loopDate = new Date(item.StartTime);
                    loopDate.setDate(loopDate.getDate() + 1);
                    loopDate.setHours(0, 0, 0, 0);

                    while (loopDate < now) {                        
                        const isToday = this.dateService.isSameDate(loopDate, now);

                        const start = new Date(loopDate.toDateString());                        
                        const end = isToday ? now : new Date(loopDate.getFullYear(), loopDate.getMonth(), loopDate.getDate(), 23, 59, 59);
                        
                        result.push({
                            Start: start,
                            End: end,
                            Channel: item.ChannelId,
                            StreamType: VideoStreamType.Main
                        });

                        loopDate.setDate(loopDate.getDate() + 1);
                    }

                }
            }
        }
        
        return result.sort((a,b) => new Date(b.End).getTime() - new Date(a.End).getTime());
    }
}
